import { cn } from '@/utils';

import type { Poll } from './Poll.types';
import type { PollResultsData } from './PollInteractionContext';
import {
  feedbackLink,
  inlineResultItem,
  inlineSeparator,
  listResultItem,
  progressBar,
  progressFill,
  progressFillCorrect,
  progressFillDefault,
  progressFillIncorrect,
  redirectLink,
  resultIcon,
  resultItem,
  resultItemCorrect,
  resultItemIncorrect,
  resultItemSelected,
  resultItemUnselected,
  resultItemVoting,
  resultLabel,
  resultPercentage,
  resultsContainer,
  resultsHeader,
  resultsList,
  resultsListBullet,
  resultsListNumbered,
} from './PollResults.css';

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgb(34, 197, 94)" strokeWidth="2">
    <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgb(239, 68, 68)" strokeWidth="2">
    <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SelectedIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" opacity={0.5}>
    <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

interface PollResultsProps {
  poll: Poll;
  resultsData: PollResultsData;
  selectedChoiceId: string;
  translations?: Record<string, string>;
  feedbackUrl?: string;
}

interface ChoiceResultProps {
  choiceId: string;
  label: string;
  isSelected: boolean;
  isTrivia: boolean;
  isTriviaAnswer: boolean;
  percentage: number;
  percentageLabel: string | undefined;
  hasLiveMetrics: boolean;
}

function getChoiceIcon({
  isSelected,
  isTrivia,
  isTriviaAnswer,
}: Pick<ChoiceResultProps, 'isSelected' | 'isTrivia' | 'isTriviaAnswer'>) {
  if (isTrivia && isTriviaAnswer) return <CheckIcon />;
  if (isSelected && isTrivia && !isTriviaAnswer) return <XIcon />;
  if (isSelected && !isTrivia) return <SelectedIcon />;
  return null;
}

function resolveTriviaAnswerId(poll: Poll, pollChoices: PollResultsData['poll_choices']): string | undefined {
  if (poll.poll_type !== 'trivia' || !poll.trivia_answer) return undefined;
  return pollChoices.find((c) => c.label === poll.trivia_answer)?.id;
}

function buildChoiceData(resultsData: PollResultsData, poll: Poll, selectedChoiceId: string) {
  const isTrivia = poll.poll_type === 'trivia';
  const triviaAnswerId = resolveTriviaAnswerId(poll, resultsData.poll_choices);
  const hasLiveMetrics = resultsData.metrics.length > 0;

  return resultsData.poll_choices.map((choice) => {
    const isSelected = choice.id === selectedChoiceId;
    const isTriviaAnswer = choice.id === triviaAnswerId;
    const metric = resultsData.metrics.find((m) => m.label === choice.label);
    const percentage = metric?.percentage ? Number.parseFloat(metric.percentage.replace('%', '')) : 0;

    return {
      choiceId: choice.id,
      label: choice.label,
      isSelected,
      isTrivia,
      isTriviaAnswer,
      percentage,
      percentageLabel: metric?.percentage,
      hasLiveMetrics,
    };
  });
}

// Default style: bordered items with progress bars
function DefaultResults({ choices }: { choices: ChoiceResultProps[] }) {
  return (
    <ul className={resultsList}>
      {choices.map((choice) => (
        <li
          key={choice.choiceId}
          className={cn(
            resultItem,
            choice.isSelected ? resultItemSelected : resultItemUnselected,
            choice.isSelected && choice.isTrivia && choice.isTriviaAnswer && resultItemCorrect,
            choice.isSelected && choice.isTrivia && !choice.isTriviaAnswer && resultItemIncorrect,
            choice.isSelected && !choice.isTrivia && resultItemVoting,
          )}
        >
          <span className={resultLabel}>{choice.label}</span>
          {choice.hasLiveMetrics && choice.percentageLabel && (
            <span className={resultPercentage}>({choice.percentageLabel})</span>
          )}
          <span className={resultIcon}>{getChoiceIcon(choice)}</span>
          <div className={progressBar}>
            <div
              className={cn(
                progressFill,
                choice.isTrivia
                  ? choice.isTriviaAnswer
                    ? progressFillCorrect
                    : progressFillIncorrect
                  : progressFillDefault,
              )}
              style={{ width: `${choice.percentage}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

// List-based results: bullet_points, numbered_list, unstyled
function ListResults({
  choices,
  variant,
}: {
  choices: ChoiceResultProps[];
  variant: 'bullet_points' | 'numbered_list' | 'unstyled';
}) {
  const listClass =
    variant === 'bullet_points' ? resultsListBullet : variant === 'numbered_list' ? resultsListNumbered : undefined;

  return (
    <ul className={cn(resultsList, listClass)}>
      {choices.map((choice) => (
        <li
          key={choice.choiceId}
          className={cn(listResultItem, choice.isSelected ? resultItemSelected : resultItemUnselected)}
        >
          <span>{choice.label}</span>
          {choice.hasLiveMetrics && choice.percentageLabel && (
            <span className={resultPercentage}>({choice.percentageLabel})</span>
          )}
          {getChoiceIcon(choice)}
        </li>
      ))}
    </ul>
  );
}

// Inline results: horizontal with pipe separators
function InlineResults({ choices }: { choices: ChoiceResultProps[] }) {
  return (
    <span>
      {choices.map((choice, idx) => (
        <span key={choice.choiceId}>
          <span className={cn(inlineResultItem, choice.isSelected ? resultItemSelected : resultItemUnselected)}>
            <span>{choice.label}</span>
            {choice.hasLiveMetrics && choice.percentageLabel && (
              <span className={resultPercentage}>({choice.percentageLabel})</span>
            )}
            {getChoiceIcon(choice)}
          </span>
          {idx < choices.length - 1 && <span className={inlineSeparator}>|</span>}
        </span>
      ))}
    </span>
  );
}

export const PollResults = ({ poll, resultsData, selectedChoiceId, translations, feedbackUrl }: PollResultsProps) => {
  const choices = buildChoiceData(resultsData, poll, selectedChoiceId);
  const isTrivia = poll.poll_type === 'trivia';
  const isCorrect = choices.find((c) => c.isSelected)?.isTriviaAnswer ?? false;

  const headerText = isTrivia
    ? isCorrect
      ? translations?.['polls.inline.trivia_correct'] || 'Correct!'
      : translations?.['polls.inline.trivia_incorrect'] || 'Incorrect!'
    : translations?.['polls.inline.vote_recorded'] || 'Your response has been recorded.';

  const pollStyle = poll.poll_style || 'default';

  return (
    <div className={resultsContainer}>
      <div className={resultsHeader}>
        {isTrivia ? isCorrect ? <CheckIcon /> : <XIcon /> : <SelectedIcon />}
        <span>{headerText}</span>
      </div>

      {pollStyle === 'default' && <DefaultResults choices={choices} />}
      {pollStyle === 'bullet_points' && <ListResults choices={choices} variant="bullet_points" />}
      {pollStyle === 'numbered_list' && <ListResults choices={choices} variant="numbered_list" />}
      {pollStyle === 'unstyled' && <ListResults choices={choices} variant="unstyled" />}
      {pollStyle === 'inline' && <InlineResults choices={choices} />}

      {poll.redirect_url && (
        <a href={poll.redirect_url} className={redirectLink} target="_blank" rel="noopener noreferrer">
          {translations?.['polls.inline.continue'] || 'Continue'} &rarr;
        </a>
      )}
      {feedbackUrl && !poll.redirect_url && (
        <a href={feedbackUrl} className={feedbackLink} rel="noopener noreferrer">
          {translations?.['polls.inline.share_feedback'] || 'Share more feedback'} &rarr;
        </a>
      )}
    </div>
  );
};
